import { useState, useEffect, useCallback, useMemo } from "react";

import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { RajasthanGenData } from "../../../models/rajasthan";
import {
  getRajasthanGenerationData,
  getRajasthanIsgsData,
  getRajReData,
} from "../../../Rest_api/DemoRestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { RAJ_UNITS } from "../const_raj";
import { convertToString } from "../../../extensions/date";
import { titleCase } from "../../../extensions/extension";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { FilteredOutEntity } from "./Data";
import { useSelector } from "react-redux";
import SecondaryHeader from "../Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { BrushStart } from "../../../models/chart_model";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./OverviewSummary";
import { RajasthanPeakAnalysis } from "./OverviewPeak";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const INTERVAL = [
  {
    label: "Time Slot",
    key: "slot",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
  {
    label: "Station",
    key: "station",
    name: "Station",
    firstKey: "station",
    secondKey: undefined,
    values: ["station"],
    isTime: false,
  },
  {
    label: "Area",
    key: "sector",
    name: "Area",
    firstKey: "sector",
    secondKey: undefined,
    values: ["sector"],
    isTime: false,
  },
  {
    label: "State",
    key: "state",
    name: "State",
    firstKey: "state",
    secondKey: undefined,
    values: ["state"],
    isTime: false,
  },
  {
    label: "Source",
    key: "source",
    name: "Source",
    firstKey: "source",
    secondKey: undefined,
    values: ["source"],
    isTime: false,
  },
];

const GROUP_BY = [
  {
    label: "Interval",
    key: "",
    values: [],
  },
];
const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[2]];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Station",
    key: "station",
    isTime: false,
    values: ["station"],
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const CONTENT_TYPE = ["Chart", "Table"];
const GENERATION_TYPE = ["Interstate", "Intrastate"];
const keys = ["onBarDS", "onBarDc", "backdown", "offBarWarm"];

const onLoad = [
  {
    value: "onBarDS",
    label: "Generation",
    color: ExchangeColors[0],
    type: "onLoad",
  },
];
const offLoad = [
  {
    value: "onBarDc",
    label: "Capacity (Declared)",
    color: ExchangeColors[2],
    type: "offLoad",
    yAxisId: "right",
  },
  {
    value: "backdown",
    label: "Backdown",
    color: ExchangeColors[1],
    type: "offLoad",
  },
];

export function RajGenOverview() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [data, setData] = useState<RajasthanGenData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const stateOptions = useMemo(
    () =>
      states.map((s, idx) => ({
        value: s,
        label: `State${idx + 1}`,
      })),
    [states],
  );
  const [uniqueSource, setUniqueSource] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string[]>([]);
  const [selectedKey, setselectedKey] = useState(INTERVAL[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [generationStatus, setGenerationStatus] = useState<string[]>([]);
  const [selectedGenerationType, setSelectedGenerationType] =
    useState(GENERATION_TYPE);
  const [selectedUnit, setSelectedUnit] = useState(RAJ_UNITS[0]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);

  const getPlantBaseName = useCallback((fullName: string) => {
    return fullName
      .replace(" Intrastate", "")
      .replace(" Interstate", "")
      .trim();
  }, []);

  const stationOptions = useMemo(() => {
    return uniqueStations.map((station, idx) => ({
      value: station,
      label: `Utility${idx + 1}`,
      plant_name: station,
    }));
  }, [uniqueStations]);

  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    const [intraRaw, reRaw, isgsRaw] = await Promise.all([
      getRajasthanGenerationData(startDateStr, endDateStr),
      getRajReData({
        startDate: convertToString(dates.startDate),
        endDate: convertToString(dates.endDate),
      }),
      getRajasthanIsgsData({
        startDate: convertToString(dates.startDate),
        endDate: convertToString(dates.endDate),
      }),
    ]);

    const intraData = intraRaw.map((item) => ({
      ...item,
      sector: "Intrastate",
      source: titleCase(titleCase(item.source)),
      station: item.station + " Intrastate",
      plant_name: item.station,
    }));

    const reGenData = reRaw
      .map((item) => ({
        ...item,
        source: titleCase(item.energy_type),
        onBarDS_mw: item.value_mw,
        onBarDS_mwh: item.value_mw / 4,
        onBarDc_mw: 0,
        onBarDc_mwh: 0,
        backdown_mw: 0,
        backdown_mwh: 0,
        station: `${titleCase(item.energy_type)} RE ${item.area}`,
        plant_name: `${titleCase(item.energy_type)} RE ${item.area}`,
        sector: item.area === "InterState" ? "Interstate" : "Intrastate",
        slot: item.time_block,
        state: "Demo RE",
      }))
      .filter((item) => item.area === "IntraState");

    const isgsData = isgsRaw.map((item) => ({
      ...item,
      onBarDS_mw: item.value_mw,
      source: titleCase(item.from_gen__generation_source),
      onBarDS_mwh: item.value_mw / 4,
      onBarDc_mw: 0,
      onBarDc_mwh: 0,
      backdown_mw: 0,
      backdown_mwh: 0,
      station: item.from_utility__name + " Interstate",
      plant_name: item.from_utility__name,
      sector: "Interstate",
    }));

    const newData: RajasthanGenData[] = [
      ...reGenData,
      ...intraData,
      ...isgsData,
    ];
    setData(newData);
    setUniqueSource(
      Array.from(new Set(newData.map((item) => titleCase(item.source)))),
    );
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        ),
      ),
    );
    setUniqueStations(
      Array.from(
        new Set(
          newData.map(
            (item) => item.plant_name || getPlantBaseName(item.station),
          ),
        ),
      ),
    );
    setStates(Array.from(new Set(newData.map((item) => item.state))).sort());
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.slot))));
    setLoading(false);
  }, [activeTab, DefaultDates, getPlantBaseName]);

  useEffect(() => {
    let filteredData = data;

    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(titleCase(item.source)),
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot),
      );
    }
    if (selectedGenerationType.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedGenerationType.includes(item.sector!),
      );
    }
    if (selectedState.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedState.includes(item.state),
      );
    }

    setUniqueStations(
      Array.from(
        new Set(
          filteredData.map(
            (item) => item.plant_name || getPlantBaseName(item.station),
          ),
        ),
      ),
    );

    if (selectedStation.length > 0) {
      const selectedStationValues = selectedStation.map((s: any) =>
        getPlantBaseName(s?.value || s?.plant_name || s),
      );
      filteredData = filteredData.filter((item) =>
        selectedStationValues.includes(
          getPlantBaseName(item.plant_name || item.station),
        ),
      );
    }

    const uniqueTimeSlotAndDate = Array.from(
      new Set(data.map((item) => item.date + " " + item.slot)),
    );
    if (
      selectedKey.firstKey === "station" ||
      selectedKey.firstKey === "sector" ||
      selectedKey.firstKey === "state" ||
      selectedKey.firstKey === "source"
    ) {
      filteredData = filteredData.map((item) => ({
        ...item,
        onBarDc_mwh: item.onBarDc_mw! / uniqueTimeSlotAndDate.length,
      }));
    } else if (selectedKey.firstKey === "date") {
      filteredData = filteredData.map((item) => ({
        ...item,
        onBarDc_mwh: item.onBarDc_mw! / 96,
      }));
    } else if (selectedKey.firstKey === "month") {
      filteredData = filteredData.map((item) => ({
        ...item,
        onBarDc_mwh: item.onBarDc_mw! / (96 * 30),
      }));
    }

    const selectedFilterStations = selectedStation.filter((item) =>
      FilteredOutEntity.includes(item.label),
    );
    if (selectedFilterStations.length === 0) {
      filteredData = filteredData.filter(
        (item) => !FilteredOutEntity.includes(item.station),
      );
    }

    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate,
    );

    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      tableKeys: ["date", "slot", "station", "sector"],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: [
        "offBarWarm_mw",
        "onBarDS_mwh",
        "onBarDc_mwh",
        "backdown_mwh",
        "offBarWarm_mwh",
      ],
      sortby: selectedKey.firstKey === "station" ? ["_onBarDS_mw"] : undefined,
      reverseSort: ["date", "slot"],
      priceKey: "",
      averageDuplicationKeys: ["date", "slot"],
      averageKeys: ["onBarDS_mw", "onBarDc_mw", "backdown_mw", "offBarWarm_mw"],
      data: filteredData,
    };

    const cData = FormatChartData(par);

    setTableData(cData.tableData || []);
    setChartKeys(keys);
    setChartData(cData.data);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedStation,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
    selectedKey,
    selectedState,
    selectedUnit,
    selectedSource,
    selectedGenerationType,
    DefaultDates,
    activeTab,
    getPlantBaseName,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData, DefaultDates, activeTab]);

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function summaryExtraKeys() {
    if (selectedKey.key === "slot") return ["slot", "date"];
    if (selectedKey.key === "date") return ["date"];
    return selectedKey.values;
  }

  const summaryExtraLabels = selectedKey.values.map((value) =>
    value === "date" ? "Date" : value,
  );
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(getLegends());

  const tablePar = {
    legends: visibleLegends,
    filename: AddDatesWithFileName({
      fileName: "Generation Overview",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
  };

  function handleKeyChange(index: number) {
    setselectedKey(INTERVAL[index]);
  }

  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={setSelectedUnit}
        selectedSource={selectedSource}
        onSourceChange={setSelectedSource}
        selectedGenerationType={selectedGenerationType}
        onGenerationTypeChange={setSelectedGenerationType}
        selectedState={selectedState}
        onStateChange={setSelectedState}
        stateOptions={stateOptions}
        uniqueSource={uniqueSource}
        generationStatus={generationStatus}
        setGenerationStatus={setGenerationStatus}
        onLoad={onLoad}
        offLoad={offLoad}
        selectedBlock={selectedBlock}
        setSelectedBlock={setSelectedBlock}
        selectedStation={selectedStation}
        setSelectedStation={setSelectedStation}
        uniqueStations={uniqueStations}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={setSelectedUnit}
        selectedSource={selectedSource}
        onSourceChange={setSelectedSource}
        selectedGenerationType={selectedGenerationType}
        onGenerationTypeChange={setSelectedGenerationType}
        selectedState={selectedState}
        onStateChange={setSelectedState}
        states={states}
        uniqueSource={uniqueSource}
        generationStatus={generationStatus}
        setGenerationStatus={setGenerationStatus}
        onLoad={onLoad}
        offLoad={offLoad}
        selectedBlock={selectedBlock}
        setSelectedBlock={setSelectedBlock}
        selectedStation={selectedStation}
        setSelectedStation={setSelectedStation}
        uniqueStations={uniqueStations}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => handleKeyChange(index)}
          />
          <FilterMenu
            title="Unit"
            items={RAJ_UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => setSelectedUnit(item)}
          />
          <FilterMenu
            title="Variable"
            items={onLoad.concat(offLoad).map((item) => item.label)}
            selectedItems={generationStatus}
            onSelect={(item, index) => {
              if (selectedGroupKeys.key !== "") {
                setGenerationStatus([onLoad.concat(offLoad)[index].label]);
              }
            }}
            setSelectedItems={
              selectedGroupKeys.key === "" ? setGenerationStatus : undefined
            }
          />
          <FilterMenu
            title="Source"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={setSelectedSource}
          />
          <FilterMenu
            title="Area"
            items={GENERATION_TYPE}
            setSelectedItems={setSelectedGenerationType}
            selectedItems={selectedGenerationType}
          />
          <FilterMenu
            title="State"
            items={stateOptions}
            selectedItems={selectedState}
            setSelectedItems={setSelectedState}
          />
          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => setSelectedChartType(CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Generation Overview" subtitle="Fetching Demo State generation overview data…" /> : (<>
          <div className="gas-heading">
            <h2 className="chartHeading w-80">Generation Overview</h2>
            <div className="flex gap-x-2 w-full justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <SearchBox
                options={stationOptions.filter(
                  (item) => !item.value.toString().includes("RE IntraState"),
                )}
                isMany={true}
                placeholder="Search Station"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedKey.firstKey === "slot"}
              onlyTitle={true}
              barCategoryGap={20}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              secondYAxisLabel={`Capacity Declared (${selectedUnit})`}
              legends={getLegends()}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey={selectedKey.firstKey}
              xTick={
                selectedKey.secondKey !== undefined &&
                selectedBlock.length === 0
                  ? (value: any, index: any) => renderHourTick(value, 10)
                  : undefined
              }
              secondXDataKey={selectedKey.secondKey}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getLegends() {
    return getSelectedUnits().map((item) => ({
      dataKey: "_" + item.value + "_" + selectedUnit.toLowerCase(),
      name: item.label.includes("Capacity") ? "Capacity" : item.label,
      legendColor: undefined,
      stroke: stringToConsistentColor(item.value),
      type: selectedChartType.value,
      stackId: selectedChartType.stackId,
    }));
  }

  function getSelectedUnits(): {
    value: string;
    label: string;
    color: string;
    type: string;
  }[] {
    const filtered = offLoad
      .concat(onLoad)
      .filter((item) => generationStatus.includes(item.label));
    return filtered.length > 0 ? filtered.reverse() : onLoad.concat(offLoad);
  }
}
